import React, { useState, useEffect } from 'react';
import { useStore } from '../utils/store';
import { CounterData } from '../types';

const CounterPanel: React.FC = () => {
  const { 
    counters, 
    marketingColumns, 
    addMarketingColumn, 
    editMarketingColumn,
    removeMarketingColumn,
    filterByMarketingEntry, 
    activeFilter, 
    setActiveFilter,
    selectedTokenIds,
    getFilteredCounters
  } = useStore();
  
  // State for filtered counters
  const [filteredCounters, setFilteredCounters] = useState<CounterData>(counters);
  
  // Update filtered counters when selection changes
  useEffect(() => {
    if (selectedTokenIds.length > 0) {
      setFilteredCounters(getFilteredCounters());
    } else {
      setFilteredCounters(counters);
    }
  }, [selectedTokenIds, counters, getFilteredCounters]);
  
  const [newColumnName, setNewColumnName] = useState('');
  const [editingColumn, setEditingColumn] = useState<string | null>(null);
  const [editedColumnName, setEditedColumnName] = useState('');
  
  // State for collapsible sections
  const [collapsedSections, setCollapsedSections] = useState<{ [key: string]: boolean }>({});
  
  // State for search filters
  const [searchFilters, setSearchFilters] = useState<{ [key: string]: string }>({});
  
  // State for pagination
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPages, setCurrentPages] = useState<{ [key: string]: number }>({});
  
  // State for compact view
  const [compactView, setCompactView] = useState(false);
  
  // State for showing only selected tokens data
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  
  // Get the counters to display based on selection mode
  const displayCounters = showOnlySelected && selectedTokenIds.length > 0 
    ? filteredCounters 
    : counters;
  
  // Handle adding a new marketing column
  const handleAddColumn = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newColumnName.trim() !== '' && !marketingColumns.includes(newColumnName.trim())) {
      addMarketingColumn(newColumnName.trim());
      setNewColumnName('');
    }
  };

  // Handle starting to edit a column name
  const handleStartEditColumn = (columnName: string) => {
    setEditingColumn(columnName);
    setEditedColumnName(columnName);
  };

  // Handle saving edited column name
  const handleSaveColumnEdit = (oldName: string) => {
    if (editedColumnName.trim() !== '' && 
        editedColumnName.trim() !== oldName && 
        !marketingColumns.includes(editedColumnName.trim())) {
      editMarketingColumn(oldName, editedColumnName.trim());
    }
    setEditingColumn(null);
    setEditedColumnName('');
  };

  // Handle canceling column edit
  const handleCancelColumnEdit = () => {
    setEditingColumn(null);
    setEditedColumnName('');
  };

  // Handle removing a column
  const handleRemoveColumn = (columnName: string) => {
    if (window.confirm(`Are you sure you want to remove the "${columnName}" column? This action cannot be undone.`)) {
      removeMarketingColumn(columnName);
    }
  };

  // Handle clicking on a counter item to filter the main table
  const handleCounterClick = (column: string, entry: string) => {
    if (activeFilter && activeFilter.column === column && activeFilter.entry === entry) {
      // If clicking the same filter, clear it
      setActiveFilter(null);
    } else {
      // Set new filter
      filterByMarketingEntry(column, entry);
    }
  };
  
  // Toggle section collapse
  const toggleSection = (column: string) => {
    setCollapsedSections({
      ...collapsedSections,
      [column]: !collapsedSections[column]
    });
  };
  
  // Handle search input change
  const handleSearchChange = (column: string, value: string) => {
    setSearchFilters({
      ...searchFilters,
      [column]: value.toLowerCase()
    });
    // Reset to first page when searching
    setCurrentPages({
      ...currentPages,
      [column]: 1
    });
  };
  
  // Handle page change
  const handlePageChange = (column: string, page: number) => {
    setCurrentPages({
      ...currentPages,
      [column]: page
    });
  };
  
  // Toggle showing only selected tokens data
  const handleToggleShowSelected = () => {
    setShowOnlySelected(!showOnlySelected);
  };
  
  // Get filtered and paginated entries for a column
  const getFilteredEntries = (column: string) => {
    const entries = Object.entries(displayCounters[column] || {});
    const searchTerm = searchFilters[column] || '';
    
    // Filter entries by search term
    const filteredEntries = searchTerm 
      ? entries.filter(([entry]) => entry.toLowerCase().includes(searchTerm))
      : entries;
    
    // Sort entries by count (descending)
    const sortedEntries = [...filteredEntries].sort((a, b) => b[1] - a[1]);
    
    // Get current page
    const currentPage = currentPages[column] || 1;
    
    // Calculate total pages
    const totalPages = Math.ceil(sortedEntries.length / itemsPerPage);
    
    // Get entries for current page
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedEntries = sortedEntries.slice(startIndex, startIndex + itemsPerPage);
    
    return {
      entries: paginatedEntries,
      totalPages,
      currentPage,
      totalEntries: sortedEntries.length
    };
  };
  
  // Render pagination controls
  const renderPagination = (column: string, totalPages: number, currentPage: number) => {
    if (totalPages <= 1) return null;
    
    return (
      <div className="flex justify-between items-center mt-2 text-xs">
        <button
          onClick={() => handlePageChange(column, Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className={`px-2 py-1 rounded ${currentPage === 1 ? 'text-gray-400' : 'text-indigo-600 hover:bg-indigo-50'}`}
        >
          Previous
        </button>
        <span className="text-gray-500">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => handlePageChange(column, Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className={`px-2 py-1 rounded ${currentPage === totalPages ? 'text-gray-400' : 'text-indigo-600 hover:bg-indigo-50'}`}
        >
          Next
        </button>
      </div>
    );
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">Marketing Counters</h2>
          
          <div className="flex space-x-2">
            <button
              onClick={handleToggleShowSelected}
              className={`inline-flex items-center px-3 py-1.5 border text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                showOnlySelected && selectedTokenIds.length > 0
                  ? 'border-indigo-500 text-white bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500'
                  : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-indigo-500'
              }`}
              disabled={selectedTokenIds.length === 0}
              title={selectedTokenIds.length === 0 ? "Select tokens to enable filtering" : (showOnlySelected ? "Show all data" : "Show only selected tokens data")}
            >
              {showOnlySelected ? "Show All" : "Show Selected"}
            </button>
            
            <button
              onClick={() => setCompactView(!compactView)}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              title={compactView ? "Switch to normal view" : "Switch to compact view"}
            >
              {compactView ? "Normal View" : "Compact View"}
            </button>
          </div>
        </div>
        
        {/* Selected tokens indicator */}
        {selectedTokenIds.length > 0 && (
          <div className="mb-4 bg-blue-50 p-2 rounded-md border border-blue-200 flex justify-between items-center">
            <span className="text-sm text-blue-700">
              {selectedTokenIds.length} token{selectedTokenIds.length !== 1 ? 's' : ''} selected
            </span>
            {showOnlySelected && (
              <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                Showing filtered data
              </span>
            )}
          </div>
        )}
        
        {/* Add New Marketing Column Form */}
        <form onSubmit={handleAddColumn} className="mb-4 bg-gray-50 p-3 rounded-lg border border-gray-200">
          <label htmlFor="newColumnName" className="block text-sm font-medium text-gray-700 mb-1">
            Add New Marketing Column
          </label>
          <div className="flex">
            <input
              type="text"
              id="newColumnName"
              value={newColumnName}
              onChange={(e) => setNewColumnName(e.target.value)}
              placeholder="New Marketing Column"
              className="flex-1 border border-gray-300 rounded-l-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-r-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Add
            </button>
          </div>
        </form>
        
        {/* Items per page selector */}
        <div className="flex items-center justify-between mb-4 bg-gray-50 p-2 rounded-lg border border-gray-200">
          <span className="text-sm text-gray-600">
            {marketingColumns.length} marketing columns
          </span>
          
          <div className="flex items-center">
            <label htmlFor="itemsPerPage" className="text-sm text-gray-600 mr-2">
              Items per page:
            </label>
            <select
              id="itemsPerPage"
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
        
        {/* Marketing Counters */}
        <div className={`space-y-2 max-h-[calc(100vh-300px)] overflow-y-auto pr-1 ${compactView ? 'text-xs' : ''}`}>
          {marketingColumns.map((column) => {
            const isCollapsed = collapsedSections[column];
            const { entries, totalPages, currentPage, totalEntries } = getFilteredEntries(column);
            
            return (
              <div key={column} className="bg-gray-50 rounded-md overflow-hidden border border-gray-200">
                <div className={`flex justify-between items-center ${compactView ? 'p-2' : 'p-3'} bg-gray-100`}>
                  <div className="flex items-center">
                    <button
                      onClick={() => toggleSection(column)}
                      className="mr-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                    >
                      {isCollapsed ? '►' : '▼'}
                    </button>
                    {editingColumn === column ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={editedColumnName}
                          onChange={(e) => setEditedColumnName(e.target.value)}
                          className="border border-gray-300 rounded-md shadow-sm py-1 px-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                          autoFocus
                        />
                        <button
                          onClick={() => handleSaveColumnEdit(column)}
                          className="text-xs bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancelColumnEdit}
                          className="text-xs bg-gray-500 text-white px-2 py-1 rounded hover:bg-gray-600"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <h3 className={`${compactView ? 'text-sm' : 'text-md'} font-medium text-gray-900 truncate max-w-[120px]`} title={column}>
                        {column}
                      </h3>
                    )}
                    <span className="ml-2 text-xs text-gray-500">({totalEntries})</span>
                  </div>
                  
                  {!editingColumn && (
                    <div className="flex space-x-1">
                      <button
                        onClick={() => handleStartEditColumn(column)}
                        className="text-xs text-indigo-600 hover:text-indigo-900 px-1.5 py-0.5 rounded hover:bg-indigo-50"
                      >
                        {compactView ? 'E' : 'Edit'}
                      </button>
                      <button
                        onClick={() => handleRemoveColumn(column)}
                        className="text-xs text-red-600 hover:text-red-900 px-1.5 py-0.5 rounded hover:bg-red-50"
                      >
                        {compactView ? 'X' : 'Remove'}
                      </button>
                    </div>
                  )}
                </div>
                
                {!isCollapsed && (
                  <div className={`${compactView ? 'p-2' : 'p-3'}`}>
                    {/* Search input */}
                    <div className="mb-2">
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search entries..."
                          value={searchFilters[column] || ''}
                          onChange={(e) => handleSearchChange(column, e.target.value)}
                          className="w-full border border-gray-300 rounded-md shadow-sm py-1 pl-8 pr-3 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                          <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    </div>
                    
                    {/* Entries list */}
                    <div className={`space-y-1 ${compactView ? 'max-h-40' : 'max-h-60'} overflow-y-auto`}>
                      {entries.length > 0 ? (
                        entries.map(([entry, count]) => (
                          <div 
                            key={`${column}-${entry}`}
                            onClick={() => handleCounterClick(column, entry)}
                            className={`
                              flex justify-between items-center ${compactView ? 'p-1.5' : 'p-2'} rounded cursor-pointer hover:bg-gray-100
                              ${activeFilter && activeFilter.column === column && activeFilter.entry === entry ? 'bg-yellow-100' : ''}
                            `}
                          >
                            <span className={`${compactView ? 'text-xs' : 'text-sm'} text-gray-700 truncate max-w-[70%]`} title={entry}>
                              {entry}
                            </span>
                            <span className={`${compactView ? 'text-xs' : 'text-sm'} font-medium bg-indigo-100 text-indigo-800 py-0.5 px-2 rounded-full`}>
                              {count}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="text-sm text-gray-500 italic">No entries found</div>
                      )}
                    </div>
                    
                    {/* Pagination */}
                    {renderPagination(column, totalPages, currentPage)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Active Filter Indicator */}
        {activeFilter && (
          <div className="mt-4 bg-yellow-50 p-3 rounded-md border border-yellow-200">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <div className="flex justify-between items-start">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Filtering: {activeFilter.column} = {activeFilter.entry}
                  </h3>
                  <button
                    onClick={() => setActiveFilter(null)}
                    className="ml-2 bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full text-xs font-medium hover:bg-yellow-200"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CounterPanel; 